import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { sqWorkbookStore, sqWorksheetStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import { useHasAssetGroups } from '@/licenseManagement/licenseManagement.store';
import { navigateToTreeBuilderPage } from '@/main/routing.utilities';
import { SearchGroupHeader } from '@/search/SearchGroupHeader.atom';
import { SearchResult } from '@/search/SearchResult.molecule';
import { clearSelection, searchOrExplore } from '@/search/search.actions';
import { LIMIT_IN_OVERVIEW, SEARCH_RESULT_TYPES } from '@/search/search.constants';
import { useSearchActions, useSearchSelectedItemIds, useSearchSelectedItemTable } from '@/search/search.store';
import { canModifyWorkbook } from '@/services/authorization.service';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { LOAD_STATUS } from '@/workbook/workbook.constants';
import { setDisplayAssetGroupEditor, setDisplayTableDefinitionEditor } from '@/worksheet/worksheet.actions';
import { faExclamationTriangle, faHouseChimney, faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Button, Icon } from '@seeqdev/qomponents';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import type { SearchModes, SearchPanes } from '@/search/search.constants';
import type { SelectedSearchItem } from '@/search/search.types';

export interface SearchGroupProps {
  loadStatus: keyof typeof LOAD_STATUS;
  label: string;
  mode: SearchModes;
  searchMode: SearchModes;
  searchTypes: string[];
  scopeIds: string[];
  restrictExploration: boolean;
  onErrorClick: () => void;
  onClickItem: (item: SelectedSearchItem) => void;
  items: any[];
  pane: SearchPanes;
  indicateSelection?: boolean;
  selectedItemId?: string;
  searchResultIcons: (item: SelectedSearchItem) => Promise<ReactNode>;
  noItemsMessage?: string;
  isSelectingAsset?: boolean;
  predefinedSearchTypes?: string[];
  totalItemsCount?: number;
  onSetModeIconClick?: () => void;
  showHomeIcon?: boolean;
  onHomeIconClick?: () => void;
  hideCreateButton?: boolean;
  /** Template creation is supplied by the template library, outside the reusable search UI. */
  templateCreateButton?: React.ReactNode;
  showPreviewBadge?: boolean;
  showNewBadge?: boolean;
  disableSearchGroup?: boolean;
  tooltipLink?: string;
  onTooltipClicked?: () => void;
}

export const SearchGroup = ({
  items,
  loadStatus,
  label,
  mode,
  pane,
  onErrorClick,
  onClickItem,
  searchResultIcons,
  searchMode,
  searchTypes,
  scopeIds,
  restrictExploration,
  noItemsMessage,
  indicateSelection = false,
  selectedItemId,
  isSelectingAsset = false,
  predefinedSearchTypes,
  totalItemsCount = 0,
  onSetModeIconClick,
  showHomeIcon = false,
  onHomeIconClick,
  showPreviewBadge = false,
  hideCreateButton = false,
  templateCreateButton,
  showNewBadge = false,
  disableSearchGroup = false,
  tooltipLink,
  onTooltipClicked,
}: SearchGroupProps) => {
  const { t } = useTranslation();
  const displayAssetGroupEditor = useFluxPath(sqWorksheetStore, (store) => store.displayAssetGroupEditor);
  const displayScalingTableEditor = useFluxPath(sqWorksheetStore, (store) => store.displayTableDefinitionView);
  const workbookId = useFluxPath(sqWorkbookStore, (store) => store.workbookId);
  const hasAssetGroups = useHasAssetGroups();

  const selectedItemIds = useSearchSelectedItemIds();
  const selectedItemTable = useSearchSelectedItemTable();
  const { setMode } = useSearchActions();
  const showCreateAssetGroupButton =
    searchMode === 'asset-groups' && hasAssetGroups && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook);
  const showCreateScalingTableButton =
    !disableSearchGroup &&
    searchMode === 'scaling-tables' &&
    pane === 'main' &&
    canModifyWorkbook(sqWorkbookStore.workbook);
  const showCreateTemplateButton =
    searchMode === 'models' && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook) && !hideCreateButton;
  const showItemTableButton =
    searchMode === 'item-tables' && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook) && !hideCreateButton;
  const displayHierarchyView = searchMode === 'item-hierarchies' && pane === 'main' && !!selectedItemTable;
  const showCreateHierarchyButton =
    displayHierarchyView && canModifyWorkbook(sqWorkbookStore.workbook) && !hideCreateButton;
  const searchingUsages = mode === 'usages';

  const setModeClick = () => {
    if (onSetModeIconClick) {
      onSetModeIconClick();
    } else {
      setMode(searchMode);
      if (searchMode !== 'usages') {
        void searchOrExplore(pane, searchMode, searchTypes, restrictExploration, scopeIds);
      }
    }
  };

  const renderAssetGroupButton = (
    <div>
      <Button
        testId="createButton"
        label={t('SEARCH_DATA.CREATE')}
        onClick={() => {
          setDisplayAssetGroupEditor(true);
        }}
        size="xs"
        disabled={displayAssetGroupEditor}
        extraClassNames="tw:mr-1"
      />
    </div>
  );

  const renderScalingTableButton = (
    <div>
      <Button
        testId="scalingTableCreateButton"
        label={t('SEARCH_DATA.CREATE')}
        size="xs"
        onClick={() => {
          doTrack(TRACK.SCALING_TABLE_CONFIGURATION, 'Open Create Item Finder Modal');
          setDisplayTableDefinitionEditor(true);
          resetItemFinder();
        }}
        disabled={displayScalingTableEditor}
        extraClassNames="tw:mr-1"
      />
    </div>
  );

  // Shared by the Item Table and Hierarchy create flows, which both open the Tree Builder page with
  // different navigation params and tracking details.
  const renderTreeBuilderCreateButton = ({
    testId,
    extraClassNames,
    onCreateClick,
  }: {
    testId: string;
    extraClassNames: string;
    onCreateClick: (event: MouseEvent | KeyboardEvent) => void;
  }) => (
    <div>
      <Button
        testId={testId}
        label={t('SEARCH_DATA.CREATE')}
        onClick={onCreateClick}
        size="xs"
        extraClassNames={extraClassNames}
      />
    </div>
  );

  const renderItemTableButton = renderTreeBuilderCreateButton({
    testId: 'itemTableCreateButton',
    extraClassNames: 'tw:mr-1',
    onCreateClick: (event) => {
      doTrack(TRACK.TREE_BUILDER, 'Create Button Clicked');
      navigateToTreeBuilderPage({ scopedTo: workbookId, event });
    },
  });

  const renderTemplateButton = <div>{templateCreateButton}</div>;

  const renderHierarchyButton = renderTreeBuilderCreateButton({
    testId: 'hierarchyCreateButton',
    extraClassNames: 'pr5 mr5',
    onCreateClick: (event) => {
      doTrack(TRACK.TREE_BUILDER, 'Create Hierarchy', { itemTableId: selectedItemTable?.id });
      navigateToTreeBuilderPage({ tableId: selectedItemTable?.id, newHierarchy: true, event });
    },
  });

  const renderButton = () => {
    if (showCreateAssetGroupButton) {
      return renderAssetGroupButton;
    }
    if (showCreateScalingTableButton) {
      return renderScalingTableButton;
    }
    if (showItemTableButton) {
      return renderItemTableButton;
    }
    if (showCreateTemplateButton) {
      return renderTemplateButton;
    }
    if (showCreateHierarchyButton) {
      return renderHierarchyButton;
    }
    return null;
  };

  const nonClickableHeading = (
    <span
      data-testid="nonClickableHeading"
      className="flexColumnContainer heading text-interactive fs15 tw:cursor-auto! tw:items-center tw:justify-between">
      <span>
        <SearchGroupHeader
          testId="nonClickableHeadingLabel"
          showPreviewBadge={showPreviewBadge}
          showNewBadge={showNewBadge}
          disableSearchGroup={disableSearchGroup}
          label={t(`SEARCH_DATA.${label}`)}
        />
        {disableSearchGroup && (
          <span>
            {tooltipLink && (
              <a target="_blank" className="tw:mt-sq-15" href={tooltipLink} onClick={onTooltipClicked} rel="noopener">
                <Icon icon={faQuestionCircle} extraClassNames="fa-lg text-interactive ml5" testId="tooltipIcon" />
              </a>
            )}
          </span>
        )}
      </span>
      {showCreateAssetGroupButton && renderAssetGroupButton}
      {showCreateScalingTableButton && renderScalingTableButton}
      {showItemTableButton && renderItemTableButton}
      {showCreateTemplateButton && renderTemplateButton}
      {showCreateHierarchyButton && renderHierarchyButton}
    </span>
  );

  const loadingSpinner = (
    <div className="flexColumnContainer tw:flex-none tw:items-center tw:justify-center tw:pt-sq-10 tw:pb-sq-10">
      <IconWithSpinner spinning={true} large={true} />
      <span className="tw:mt-sq-5 tw:mb-sq-5 tw:ml-sq-5">{t(`SEARCH_DATA.LOADING_${label.replace('.', '_')}`)}</span>
    </div>
  );

  // `loadStatus` is typed as "ERROR" | "LOADED" | "LOADING" | "NOT_LOADED" from keyof typeof LOAD_STATUS. But
  // `SEARCH_RESULT_TYPES.ASSETS` is typed as 'assets', so we need to use `as any` here.
  const isLoadingHierarchies = (loadStatus as any) === SEARCH_RESULT_TYPES.ASSETS && mode === 'item-hierarchies';

  return disableSearchGroup ? (
    <div data-testid="disabledStatus">{nonClickableHeading}</div>
  ) : (
    <div>
      {loadStatus === LOAD_STATUS.LOADING && !searchingUsages && (
        <div data-testid="loadingStatus">
          {nonClickableHeading}
          {loadingSpinner}
        </div>
      )}

      {loadStatus === LOAD_STATUS.ERROR && !searchingUsages && (
        <div data-testid="errorStatus">
          {nonClickableHeading}
          <div className="flexColumnContainer sq-text-danger cursorPointer tw:pt-sq-3 tw:pb-1.5" onClick={onErrorClick}>
            <Icon icon={faExclamationTriangle} type="danger" extraClassNames="flexCenter pl5 pr10 pt2" />
            <div className="flexFill tw:pl-sq-5">
              {t(`SEARCH_DATA.LOAD_${label.replace('.', '_')}_FAILED`)}
              <br />
              <span className="tw:underline!">{t('SEARCH_DATA.RETRY')}</span>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 &&
        ((mode === searchMode && !searchingUsages) || mode === 'overview') &&
        noItemsMessage &&
        pane === 'main' && (
          <div>
            {nonClickableHeading}
            {isLoadingHierarchies ? loadingSpinner : <div className="tw:m-sq-5">{t(noItemsMessage)}</div>}
          </div>
        )}

      {items.length > 0 && (mode === searchMode || mode === 'overview') && (
        <div data-testid={`searchGroup-${label}`}>
          {mode === 'overview' && (
            <div data-testid="modeSet" className="flexColumnContainer heading tw:items-center tw:justify-between">
              {showHomeIcon && (
                <div data-testid="homeIconContainer">
                  <Icon
                    icon={faHouseChimney}
                    extraClassNames="pl5 pr2"
                    testId="breadcrumbHomeIcon"
                    onClick={onHomeIconClick}
                  />
                  <span className="tw:pr-0.5">»</span>
                </div>
              )}
              <span className="text-interactive flexFill fs15">
                <SearchGroupHeader
                  showPreviewBadge={showPreviewBadge}
                  showNewBadge={showNewBadge}
                  disableSearchGroup={disableSearchGroup}
                  label={`${t(`SEARCH_DATA.${label}`)} ${totalItemsCount > 0 ? `(${totalItemsCount})` : ''}`}
                  setModeClick={setModeClick}
                />
              </span>
              {renderButton()}
            </div>
          )}
          {mode === 'item-hierarchies' && displayHierarchyView && <>{nonClickableHeading}</>}

          {_.map(items, (item: any) => (
            <SearchResult
              isSelectingAsset={isSelectingAsset}
              searchTypes={predefinedSearchTypes}
              key={item.id}
              item={item}
              items={items}
              searchResultIcons={searchResultIcons}
              itemSelected={_.indexOf(selectedItemIds, item.id) > -1}
              onClickItem={() => {
                clearSelection(); // unselect selected items
                onClickItem(item);
              }}
              isSelected={indicateSelection && selectedItemId === item.id}
              noNavigate={restrictExploration}
            />
          ))}
        </div>
      )}
      {mode === 'overview' && totalItemsCount > LIMIT_IN_OVERVIEW && (
        <Button
          testId="viewMoreButton"
          label={t('SEARCH_DATA.VIEW_MORE')}
          onClick={() => setModeClick()}
          extraClassNames="viewMoreLinkButton"
        />
      )}
    </div>
  );
};
